/* ============================================================
   Gurvaya Animations — site-wide fade-in, parallax, steam
   ============================================================ */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let parallaxBound = false;
  let fadeObserver = null;

  const FADE_BLOCK_SELECTORS = [
    '.gurvaya-story',
    '.gurvaya-promise',
    '.gurvaya-ritual',
    '.gurvaya-blends__header',
    '.gurvaya-blend-card',
    '.gurvaya-faq__header',
    '.gurvaya-faq__item',
    '.gurvaya-faq__closing',
    '.gurvaya-promise-videos__header',
    '.gurvaya-promise-videos__card',
    '.gurvaya-story-page__hero',
    '.gurvaya-story-page__chapter',
    '.gurvaya-story-page__values',
    '.gurvaya-story-page__closing',
    '.gurvaya-faq-page__hero',
    '.gurvaya-faq-page__group',
    '.gurvaya-reels__header',
    '.gurvaya-reels__card',
    '.gurvaya-why__copy',
    '.gurvaya-why__media',
    '.zn-ba__header',
    '.zn-ba__compare',
    '.gurvaya-impact header',
    '.gurvaya-impact li',
  ].join(', ');

  const PARALLAX_SELECTORS = [
    '.banner__media',
    '.slideshow__media',
    '.collection-hero__image-container',
    '.gurvaya-hero__visual',
    '.gurvaya-story__media',
    '.gurvaya-story-page__hero-media',
    '.gurvaya-story-page__chapter-media',
    '.gurvaya-faq__closing',
    '.image-with-text__media',
    '.email-signup-banner .banner__media',
  ].join(', ');

  function skipSection(section) {
    if (!section) return true;
    if (section.querySelector('.header-wrapper, .announcement-bar, cart-drawer, .cart-drawer, .gurvaya-hero')) {
      return true;
    }
    const id = section.id || '';
    if (id.indexOf('announcement') !== -1 || id.indexOf('header') !== -1) return true;
    return false;
  }

  function markFade(el, order) {
    if (!el || el.hasAttribute('data-fade-in')) return;
    el.setAttribute('data-fade-in', '');
    if (typeof order === 'number') {
      el.setAttribute('data-fade-order', String(order));
    }
  }

  function prepareFadeTargets(root) {
    const scope = root || document;
    const main = scope.querySelector ? scope.querySelector('#MainContent') : document.getElementById('MainContent');
    const sections = (main || document).querySelectorAll('.shopify-section');

    sections.forEach((section) => {
      if (skipSection(section)) return;

      const innerBlocks = section.querySelectorAll(FADE_BLOCK_SELECTORS);
      if (innerBlocks.length) {
        innerBlocks.forEach((block, index) => markFade(block, index % 7));
        return;
      }

      markFade(section);
    });

    const footer = document.querySelector('.shopify-section-group-footer-group, footer, .gurvaya-footer');
    if (footer && !footer.closest('#shopify-section-header')) {
      const footerSection = footer.closest('.shopify-section') || footer;
      if (!skipSection(footerSection)) markFade(footerSection);
    }
  }

  function initFadeIn(root) {
    prepareFadeTargets(root);

    const elements = Array.from(document.querySelectorAll('[data-fade-in]')).filter(
      (el) => !el.classList.contains('is-visible')
    );
    if (!elements.length) return;

    if (prefersReducedMotion) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    if (!fadeObserver) {
      fadeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              fadeObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -48px 0px', threshold: 0.08 }
      );
    }

    elements.forEach((el) => fadeObserver.observe(el));
  }

  function initHeroFadeIn() {
    document.querySelectorAll('.gurvaya-hero').forEach((hero) => {
      if (prefersReducedMotion) {
        hero.classList.add('gurvaya-hero--loaded');
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          hero.classList.add('gurvaya-hero--loaded');
        });
      });
    });
  }

  function initParallax() {
    if (prefersReducedMotion) return;

    const medias = Array.from(document.querySelectorAll(PARALLAX_SELECTORS));
    if (!medias.length) return;

    medias.forEach((el) => el.setAttribute('data-parallax', ''));

    function updateParallax() {
      medias.forEach((media) => {
        const rect = media.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const shift = (progress - 0.5) * 48;
        media.style.setProperty('--parallax-y', `${shift.toFixed(2)}px`);
      });
    }

    if (!parallaxBound) {
      let ticking = false;
      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            updateParallax();
            ticking = false;
          });
        },
        { passive: true }
      );
      parallaxBound = true;
    }

    updateParallax();
  }

  function initSteam() {
    if (prefersReducedMotion) return;

    document.querySelectorAll('.gurvaya-hero__steam').forEach((container) => {
      if (container.querySelector('.steam-wisp')) return;

      const positions = [18, 30, 42, 54, 66, 76];
      const durations = [3.0, 3.6, 2.8, 4.0, 3.3, 2.5];
      const delays = [0, 0.8, 1.5, 0.3, 2.1, 1.0];

      positions.forEach((left, i) => {
        const wisp = document.createElement('div');
        wisp.className = 'steam-wisp';
        wisp.style.setProperty('--steam-dur', `${durations[i]}s`);
        wisp.style.setProperty('--steam-delay', `${delays[i]}s`);
        wisp.style.left = `${left}%`;
        container.appendChild(wisp);
      });
    });
  }

  function boot(root) {
    initHeroFadeIn();
    initFadeIn(root);
    initParallax();
    initSteam();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => boot(event.target));
  }
})();
